import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faUser, faXmark, faSparkles } from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import { StatusChipV3 } from '@labrav/react-components'
import { GradientFaIcon } from '@labrav/common'
import { FundingRun } from '../../api/fundingApi'
import { formatAmount } from '../../utils/format'
import { statusChip } from '../../utils/statusChip'
import { ReviewWithAiButton } from './ReviewWithAiButton'
import { useStyles } from './StatusBanner.styles'

export type BannerActionKey = 'contactPdm' | 'withdraw' | 'resend'

interface StatusBannerProps {
  run: FundingRun
  onReview: () => void
  onAction: (key: BannerActionKey) => void
}

interface ActionButtonProps {
  icon: IconDefinition
  label: string
  onClick: () => void
}

const ActionButton = ({ icon, label, onClick }: ActionButtonProps) => {
  const { classes } = useStyles()
  return (
    <button type="button" className={classes.actionButton} onClick={onClick}>
      {icon === faSparkles ? (
        <GradientFaIcon icon={icon} className={classes.gradientIcon} />
      ) : (
        <FontAwesomeIcon icon={icon} />
      )}
      <Typography component="span">{label}</Typography>
    </button>
  )
}

type BannerVariant = 'alert' | 'success' | 'error' | 'neutral'

interface BannerAction {
  key: BannerActionKey
  icon: IconDefinition
  label: string
}

const CONTACT_PDM: BannerAction = {
  key: 'contactPdm',
  icon: faUser,
  label: 'Contact PDM for Wallet issue',
}
const WITHDRAW: BannerAction = {
  key: 'withdraw',
  icon: faXmark,
  label: 'Withdraw request',
}
const RESEND: BannerAction = {
  key: 'resend',
  icon: faSparkles,
  label: 'Resend funding request',
}

interface BannerConfig {
  variant: BannerVariant
  staticMessage?: string
  amountLabel: string
  showReview: boolean
  actions: BannerAction[]
}

const BANNER_BY_STATUS: Record<string, BannerConfig> = {
  ACTION_REQUIRED: {
    variant: 'alert',
    amountLabel: 'Funding amount',
    showReview: true,
    actions: [CONTACT_PDM, WITHDRAW],
  },
  APPROVED: {
    variant: 'success',
    staticMessage: 'AWS has approved your funding request.',
    amountLabel: 'Funding approved',
    showReview: false,
    actions: [],
  },
  REJECTED: {
    variant: 'error',
    amountLabel: 'Funding requested',
    showReview: true,
    actions: [CONTACT_PDM, WITHDRAW, RESEND],
  },
}

const DEFAULT_BANNER: BannerConfig = {
  variant: 'neutral',
  amountLabel: 'Funding amount',
  showReview: false,
  actions: [],
}

export const StatusBanner = ({
  run,
  onReview,
  onAction,
}: StatusBannerProps) => {
  const { classes, cx } = useStyles()
  const status = run.display_status ?? run.status
  const chip = statusChip(status)
  const config = BANNER_BY_STATUS[status] ?? DEFAULT_BANNER
  const message = config.staticMessage ?? run.last_error
  const amount = run.amount == null ? null : Number(run.amount)

  const variantClass = {
    alert: classes.borderAlert,
    success: classes.borderSuccess,
    error: classes.borderError,
    neutral: classes.borderNeutral,
  }[config.variant]

  return (
    <section
      className={cx(classes.banner, variantClass)}
      data-testid="funding-detail-status"
    >
      <div className={classes.topRow}>
        <div className={classes.statusGroup}>
          <Typography component="span" className={classes.statusLabel}>
            Status
          </Typography>
          <StatusChipV3
            bgColor={chip.bg}
            textColor={chip.text}
            label={chip.label}
            sizeVarient="normal"
          />
        </div>

        {(message || config.showReview) && (
          <>
            <span className={classes.divider} />
            <div className={classes.messageGroup}>
              {message && (
                <Typography component="span" className={classes.messageText}>
                  {message}
                </Typography>
              )}
              {config.showReview && <ReviewWithAiButton onClick={onReview} />}
            </div>
          </>
        )}

        <span className={classes.divider} />
        <div className={classes.amountGroup}>
          <Typography component="span" className={classes.amountLabel}>
            {config.amountLabel}
          </Typography>
          <Typography component="span" className={classes.amountValue}>
            {amount == null ? (
              '-'
            ) : (
              <>
                <span className={classes.amountSymbol}>$</span>
                {formatAmount(amount)}
              </>
            )}
          </Typography>
        </div>
      </div>

      {config.actions.length > 0 && (
        <div className={classes.actions}>
          {config.actions.map(action => (
            <ActionButton
              key={action.key}
              icon={action.icon}
              label={action.label}
              onClick={() => onAction(action.key)}
            />
          ))}
        </div>
      )}
    </section>
  )
}
